import * as Icons from "lucide-react";
import { fmtAmount, equivalentFor } from "@/lib/currency";
import { FxDisclaimer } from "@/components/shared/DualPrice";

// One amount rendered as canonical (emphasized) + informational equivalent.
// Per-currency subtotals stay billing-accurate; equivalents are never summed across currencies.
function Money({ cur, value, suffix = "", strong, muted, light, testid, rate, showThb }) {
  const equiv = equivalentFor(value, cur, { rate, showThb });
  const base = strong ? "font-bold" : "font-medium";
  const tone = light ? "text-slate-100" : muted ? "text-slate-400" : strong ? "text-slate-900" : "text-slate-700";
  return (
    <span className="inline-flex flex-col items-end leading-tight" data-testid={testid}>
      <span className={`${base} ${tone}`}>{fmtAmount(value, cur)}{suffix}</span>
      {equiv && <span className="text-[10px] text-slate-400">≈ {equiv}{suffix}</span>}
    </span>
  );
}

function Row({ label, cur, value, suffix = "", strong, muted, light, testid, rate, showThb }) {
  return (
    <div className="flex items-start justify-between text-sm">
      <span className={strong ? "font-semibold text-slate-800" : muted ? "text-slate-400" : light ? "text-slate-300" : "text-slate-600"}>{label}</span>
      <Money cur={cur} value={value} suffix={suffix} strong={strong} muted={muted} light={light} testid={testid} rate={rate} showThb={showThb} />
    </div>
  );
}

function ServiceLines({ cur, services, rate, showThb }) {
  if (!services?.length) return null;
  return (
    <div className="space-y-1">
      {services.map((s, i) => (
        <div key={i} className="flex items-start justify-between text-[13px]">
          <span className="text-slate-600 truncate pr-2 pt-0.5">{s.name}</span>
          {s.requires_manual_price
            ? <span className="text-slate-500 shrink-0">—</span>
            : <Money cur={cur} value={s.price} muted rate={rate} showThb={showThb} />}
        </div>
      ))}
    </div>
  );
}

function CurrencyBlock({ cur, b, rate, showThb }) {
  const s = b.summary || {};
  const ot = b.one_time || {};
  const monthly = b.monthly || {};
  const annual = b.annual || {};
  const hasMonthly = (monthly.services || []).length > 0;
  const hasAnnual = (annual.services || []).length > 0;
  const quarterly = b.quarterly || {};
  const hasQuarterly = (quarterly.services || []).length > 0;
  const hasRecurring = hasMonthly || hasAnnual || hasQuarterly;
  const mp = { rate, showThb };
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`ca-eb-${cur}`}>
      <div className="bg-slate-50 px-4 py-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Engagement — {cur} <span className="text-slate-400 normal-case font-normal">(canonical)</span></span>
      </div>

      <div className="p-4 space-y-4">
        {(ot.services || []).length > 0 && (
          <div className="space-y-2" data-testid={`ca-eb-${cur}-onetime`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">One-Time Services</div>
            <ServiceLines cur={cur} services={ot.services} {...mp} />
            <div className="pt-1.5 border-t border-slate-100">
              <Row label="One-Time Services Subtotal" cur={cur} value={ot.subtotal} strong testid={`ca-eb-${cur}-onetime-subtotal`} {...mp} />
            </div>
          </div>
        )}

        {hasRecurring && (
          <div className="space-y-3" data-testid={`ca-eb-${cur}-recurring`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Recurring Services</div>
            {hasMonthly && (
              <div className="space-y-1.5">
                <div className="text-[12px] font-medium text-slate-500">Monthly Services</div>
                <ServiceLines cur={cur} services={monthly.services} {...mp} />
                <div className="pt-1 border-t border-slate-100">
                  <Row label="Monthly Recurring Total" cur={cur} value={monthly.total} suffix="/mo" strong testid={`ca-eb-${cur}-monthly-total`} {...mp} />
                </div>
              </div>
            )}
            {hasAnnual && (
              <div className="space-y-1.5">
                <div className="text-[12px] font-medium text-slate-500">Annual Services</div>
                <ServiceLines cur={cur} services={annual.services} {...mp} />
                <div className="pt-1 border-t border-slate-100">
                  <Row label="Annual Recurring Total" cur={cur} value={annual.total} suffix="/yr" strong testid={`ca-eb-${cur}-annual-total`} {...mp} />
                </div>
              </div>
            )}
            {hasQuarterly && (
              <div className="space-y-1.5">
                <div className="text-[12px] font-medium text-slate-500">Quarterly Services</div>
                <ServiceLines cur={cur} services={quarterly.services} {...mp} />
                <div className="pt-1 border-t border-slate-100">
                  <Row label="Quarterly Recurring Total" cur={cur} value={quarterly.total} suffix="/qtr" strong testid={`ca-eb-${cur}-quarterly-total`} {...mp} />
                </div>
              </div>
            )}
          </div>
        )}
        <div className="rounded-lg bg-slate-900 text-white p-4 space-y-2" data-testid={`ca-eb-${cur}-summary`}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Engagement Summary</div>
          <div className="space-y-1.5">
            <Row label="Initial One-Time Fees" cur={cur} value={s.initial_one_time_fees} light {...mp} />
            <Row label={<>One-Time Amount Due at Engagement <span className="text-[11px] text-slate-500">({ot.deposit_label}{ot.any_prepaid ? " · incl. prepaid" : ""})</span></>} cur={cur} value={s.one_time_due_at_engagement} light testid={`ca-eb-${cur}-onetime-due`} {...mp} />
            {(monthly.services || []).length > 0 && <Row label="First Month Recurring" cur={cur} value={s.first_month_recurring} light {...mp} />}
            {(annual.services || []).length > 0 && <Row label="First Annual Recurring Charge" cur={cur} value={s.first_annual_recurring} light {...mp} />}
            {hasQuarterly && <Row label="First Quarterly Recurring Charge" cur={cur} value={s.first_quarter_recurring} light {...mp} />}
          </div>
          <div className="pt-2 border-t border-slate-700 flex items-start justify-between">
            <span className="text-sm font-semibold text-white pt-1">Estimated Total Due at Engagement</span>
            <Money cur={cur} value={s.estimated_total_due_at_engagement} strong light testid={`ca-eb-${cur}-due-at-engagement`} {...mp} />
          </div>
          <div className="pt-2 border-t border-slate-700 space-y-1">
            {s.remaining_one_time_balance > 0 && (
              <Row label="Remaining One-Time Balance" cur={cur} value={s.remaining_one_time_balance} muted testid={`ca-eb-${cur}-remaining`} {...mp} />
            )}
            {(monthly.services || []).length > 0 && <Row label="Ongoing Monthly Total" cur={cur} value={s.ongoing_monthly_total} suffix="/mo" muted {...mp} />}
            {(annual.services || []).length > 0 && <Row label="Ongoing Annual Total" cur={cur} value={s.ongoing_annual_total} suffix="/yr" muted {...mp} />}
          </div>
        </div>
      </div>
    </div>
  );
}

export function EngagementBreakdown({ breakdown, rate, showThb }) {
  if (!breakdown || Object.keys(breakdown).length === 0) return null;
  const currencies = Object.keys(breakdown).sort();
  const anyEquiv = showThb || currencies.includes("THB");
  return (
    <div className="mt-5 pt-4 border-t border-slate-200 space-y-3" data-testid="ca-engagement-breakdown">
      <div className="flex items-center gap-2 flex-wrap">
        <Icons.ReceiptText className="h-4 w-4 text-slate-500" />
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Engagement Breakdown</span>
        {currencies.length > 1 && <span className="text-[11px] text-amber-600 font-medium">Multiple billing currencies — shown separately (not combined)</span>}
        <span className="text-[11px] text-slate-400">Estimate only — staff approval &amp; engagement letter are authoritative</span>
      </div>
      {currencies.map((cur) => <CurrencyBlock key={cur} cur={cur} b={breakdown[cur]} rate={rate} showThb={showThb} />)}
      {anyEquiv && <FxDisclaimer />}
    </div>
  );
}
